/**
 * @fileoverview นำเข้าสต็อกจากรายงาน SAP (sheet "Stock2") แบบ full-replace
 * @module utils/stockImport
 *
 * ไฟล์ต้นทางเป็น export จาก SAP โดยตรง: หัวคอลัมน์อยู่แถว 1 ข้อมูลเริ่มแถว 2 · จับคอลัมน์ตามชื่อหัว (ไม่ผูกตำแหน่ง)
 *
 * 1 แถวในไฟล์ = Material x Plant x Storage Location x Special Stock Type x WBS x Batch
 *   -> stocks     = Material x Plant(คลัง) x Storage Location(พื้นที่เก็บ) x Special Stock Type x WBS
 *   -> stock_lots = stock + Batch          (หลายแถวในไฟล์รวมเป็น stock แถวเดียวได้)
 *
 * โมดูลนี้ทำเฉพาะงานข้อมูล (อ่านไฟล์ / ตรวจ / ยิง API) ไม่ยุ่งกับข้อความบนหน้าจอ
 * ผู้เรียกเป็นคนแปลง error code เป็นข้อความด้วย t() เอง
 */

import * as XLSX from "xlsx";
import { GET, POST, DELETE } from "../services/api";
import { matchColumnsByHeader } from "./headerMatch";

const company = (() => {
  try {
    const raw = localStorage.getItem("cm-company");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
})();

/** ชื่อ sheet ที่ใช้ (ไฟล์เดียวกันอาจมี sheet อื่นที่คนละรูปแบบ) */
export const STOCK_IMPORT_SHEET = "Stock2";

/** แถว 1 เป็นหัวคอลัมน์ ข้อมูลจึงเริ่มอ่านจากแถว 2 */
export const STOCK_IMPORT_HEADER_ROWS = 1;

/** ส่งเข้า backend ทีละชุด ให้เห็นความคืบหน้าและไม่ยิง payload ก้อนเดียวใหญ่เกินไป */
export const STOCK_IMPORT_CHUNK_SIZE = 100;

const BASE_PATH = "warehouse/stocks/import/batches";

/**
 * คอลัมน์ตามลำดับของ template (A..N) — ตอนอ่านไฟล์จับตามชื่อ en/th ไม่ใช่ตำแหน่ง
 * required -> ต้องมีค่า, newIfMissing -> ไม่มีใน master ก็นำเข้าได้ หลังบ้านสร้างให้ตอน commit
 * WBS ไม่ตรวจกับ master: ไม่พบโครงการก็ยังนำเข้าได้ เก็บเป็นข้อความไว้
 * Material ที่ยังไม่มีใน Item Master -> สร้างอะไหล่ให้
 * (รหัสอะไหล่ = Material, ชื่อ/คำอธิบาย = Material Description, จัดประเภท 1 = "Part")
 * Plant ที่ยังไม่มีในคลังสินค้า -> สร้างคลังให้ (รหัส = Plant, ชื่อ = Plant Name)
 * Storage Location ว่างได้ (สต็อกไม่ผูกพื้นที่เก็บ) ถ้ามีรหัสแต่คลังนั้นยังไม่มีพื้นที่เก็บนี้ -> สร้างให้
 * (รหัส = Storage Location, ชื่อ = Description of Storage Location) — ตรวจเป็นคู่กับ Plant ดู parseStockWorkbook
 */
export const STOCK_IMPORT_COLUMNS = [
    // ไม่พบใน Item Master = ไม่ใช่ error ระบบจะสร้างอะไหล่ให้ตอนนำเข้า (newIfMissing -> ขึ้นหมายเหตุในพรีวิว)
    { key: "material_code", th: "รหัสวัสดุ", en: "Material", type: "text", required: true, newIfMissing: "part" },            // A
    { key: "material_name", th: "ชื่อวัสดุ", en: "Material Description", type: "text" },                                   // B
    { key: "plant_code", th: "รหัสคลัง (Plant)", en: "Plant", type: "text", required: true, newIfMissing: "warehouse" },   // C
    { key: "plant_name", th: "ชื่อคลัง", en: "Plant Name", type: "text" },                                                 // D
    { key: "storage_location_code", th: "รหัสพื้นที่เก็บ", en: "Storage Location", type: "text" },                         // E
    { key: "storage_location_name", th: "ชื่อพื้นที่เก็บ", en: "Description of Storage Location", type: "text" },          // F
    { key: "special_stock_type", th: "สต็อกพิเศษ", en: "Special Stock Type", type: "text" },                              // G
    { key: "special_stock_type_name", th: "คำอธิบายสต็อกพิเศษ", en: "Special Stock Type Description", type: "text" },      // H
    { key: "wbs_element", th: "รหัส WBS", en: "WBS Element", type: "text" },                                               // I
    { key: "wbs_element_name", th: "ชื่อ WBS", en: "WBS Element Description", type: "text" },                              // J
    { key: "batch_number", th: "Batch", en: "Batch", type: "text" },                                                       // K
    { key: "unrestricted_qty", th: "ยอดใช้ได้", en: "Unrestricted Stock", type: "number" },                                // L
    { key: "quality_inspection_qty", th: "ยอดรอตรวจ", en: "Stock in Quality Inspection", type: "number" },                 // M
    { key: "blocked_qty", th: "ยอดระงับ", en: "Blocked Stock", type: "number" },                                           // N
];

/** คอลัมน์ที่ประกอบกันเป็นคีย์จริงของไฟล์ (ไม่มีคอลัมน์ไหน unique ตัวเดียว) */
const KEY_COLUMNS = [
    "material_code",
    "plant_code",
    "storage_location_code",
    "special_stock_type",
    "wbs_element",
    "batch_number",
];

/** หมายเหตุในพรีวิวของค่าที่ยังไม่มีใน master แยกตามชนิด เพื่อบอกได้ว่าระบบจะสร้าง "อะไร" ให้ */
const NEW_NOTES = {
    part: "will_create",
    warehouse: "will_create_warehouse",
    location: "will_create_location",
};

const norm = (value) => (value === undefined || value === null ? "" : String(value).trim());

/**
 * ค่าของคอลัมน์รหัส — เอาข้อความตามที่เห็นในไฟล์เป็นหลัก เพื่อไม่ให้เลข 0 นำหน้าหาย
 *
 * เซลล์ที่เป็นตัวเลขต้องระวัง เพราะ format อาจเติมอะไรที่ไม่ใช่ส่วนหนึ่งของรหัสมาด้วย
 * (ลูกน้ำคั่นหลัก สัญลักษณ์เงิน) จึงรับข้อความที่ format ไว้เฉพาะตอนที่ยังเป็นตัวเลขล้วน
 * นอกนั้นถอยไปใช้ค่าดิบ ปลอดภัยกว่าปล่อยให้รหัสเพี้ยนแบบเงียบ ๆ
 */
const codeValue = (rawCell, shownCell) => {
    const shown = norm(shownCell);
    if (typeof rawCell !== "number") return shown || norm(rawCell);

    const cleaned = shown.replace(/[,\s]/g, "");
    return /^\d+$/.test(cleaned) ? cleaned : norm(rawCell);
};

/** "1,234" หรือ "80 EA" -> 1234 / 80 ; ค่าว่าง -> 0 ; อ่านไม่ออก -> null */
const toNumber = (raw) => {
    const s = norm(raw).replace(/,/g, "");
    if (s === "") return 0;
    const matched = s.match(/-?\d+(\.\d+)?/);
    if (!matched || !isFinite(Number(matched[0]))) return null;
    return Number(matched[0]);
};

/**
 * ดึงรหัสของ master ที่ต้องใช้ตรวจ
 * @returns {Promise<{part: Set|null, warehouse: Set|null, locationPlants: Map|null}>}
 *   locationPlants = รหัสพื้นที่เก็บ -> { plants: Set รหัสคลังที่ผูกอยู่, shared: มีตัวที่ไม่ผูกคลังไหม }
 */
const fetchLookupSets = async () => {
    const [parts, warehouses, locations] = await Promise.all([
        GET("warehouse/part-masters", { page_size: 100000, page: 0 }),
        GET("warehouse/warehouses", { page_size: 100000, page: 0 }),
        GET("warehouse/locations", { page_size: 100000, page: 0 }),
    ]);

    // ถ้าดึงไม่สำเร็จให้เป็น null เพื่อ "ไม่ตรวจ" ดีกว่าขึ้น error ปลอมทั้งไฟล์ (backend ตรวจซ้ำอยู่แล้ว)
    const setOf = (res, field) =>
        res?.status ? new Set((res.data || []).map((d) => norm(d[field])).filter(Boolean)) : null;

    // รหัสพื้นที่เก็บซ้ำข้ามคลังได้ (SAP ใช้ Storage Location รหัสเดิมในหลาย Plant)
    // จึงต้องเก็บว่าแต่ละรหัสผูกกับคลังไหนบ้าง เพื่อดูคู่ Plant + Storage Location ให้ตรงกับหลังบ้าน
    const plantsByLocation = (res) => {
        if (!res?.status) return null;

        const map = new Map();
        (res.data || []).forEach((loc) => {
            const code = norm(loc.location_code);
            if (!code) return;
            if (!map.has(code)) map.set(code, { plants: new Set(), shared: false });

            const plant = norm(loc.warehouse?.warehouse_code);
            // ไม่ผูกคลัง = พื้นที่เก็บกลาง ใช้กับคลังไหนก็ได้ (หลังบ้านใช้เป็นตัวสำรอง)
            if (plant) map.get(code).plants.add(plant);
            else map.get(code).shared = true;
        });
        return map;
    };

    return {
        part: setOf(parts, "part_number"),
        warehouse: setOf(warehouses, "warehouse_code"),
        locationPlants: plantsByLocation(locations),
    };
};

/**
 * อ่านไฟล์ Excel แล้วตรวจทีละแถว
 * @returns {Promise<{rows: Array, sheetName: string}>}
 *   rows = [{ row_no, valid, cells: { key: { raw, value, error } } }]
 *   error code: "required" | "number" | "lookup" | "duplicate"
 *   note "will_create" | "will_create_warehouse" | "will_create_location"
 *     = ค่านี้ยังไม่มีใน master ระบบจะสร้างให้ตอนนำเข้า (ไม่บล็อกการยืนยัน)
 */
export const parseStockWorkbook = async (file) => {
    const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
    const sheetName = workbook.SheetNames.includes(STOCK_IMPORT_SHEET) ? STOCK_IMPORT_SHEET : workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    const readSheet = (raw) =>
        XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            range: STOCK_IMPORT_HEADER_ROWS, // ข้ามหัวตาราง เริ่มอ่านแถวข้อมูลแรก
            defval: "",
            raw,
        });

    // อ่านสองชุดแล้วเลือกใช้คนละคอลัมน์ เพราะสองปัญหานี้แก้ด้วยโหมดเดียวกันไม่ได้:
    //   raw       = ค่าดิบ ใช้กับคอลัมน์ยอด เพราะค่าที่ผ่าน format มาแล้วอาจถูกปัดเศษ (1234.56 -> "1,235")
    //   formatted = ข้อความตามที่เห็นในไฟล์ ใช้กับคอลัมน์รหัส เพราะเซลล์ที่ตั้ง format เลข 0 นำหน้าไว้
    //               (SAP ส่งมาแบบนี้บ่อย) ถ้าอ่านดิบจะเหลือแต่ตัวเลข เช่น 0001000 -> 1000 แล้วหา master ไม่เจอ
    const raw = readSheet(true);
    const formatted = readSheet(false);

    // จับคอลัมน์ตามชื่อหัว (แถว STOCK_IMPORT_HEADER_ROWS) -> ตำแหน่งจริงในไฟล์ของแต่ละคอลัมน์
    const headerRow = (XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", range: 0 })[STOCK_IMPORT_HEADER_ROWS - 1]) || [];
    const { colIndex, missing } = matchColumnsByHeader(headerRow, STOCK_IMPORT_COLUMNS);
    if (missing.length) {
        const err = new Error(missing.join(", "));
        err.missingColumns = missing;
        throw err;
    }
    const pick = (arr, ci) => (colIndex[ci] < 0 ? "" : arr[colIndex[ci]]);

    const lookupSets = await fetchLookupSets();
    const rows = [];
    const seenKeys = new Set();

    raw.forEach((arr, idx) => {
        // ข้ามแถวว่างท้าย sheet (ไฟล์ SAP มักมีติดมา)
        if (STOCK_IMPORT_COLUMNS.every((_, ci) => norm(pick(arr, ci)) === "")) return;

        const cells = {};
        let valid = true;

        const shown = formatted[idx] || [];

        STOCK_IMPORT_COLUMNS.forEach((col, ci) => {
            const rawValue = col.type === "number" ? norm(pick(arr, ci)) : codeValue(pick(arr, ci), pick(shown, ci));
            let value = rawValue;
            let error = "";
            let note = "";

            if (col.type === "number") {
                const parsed = toNumber(rawValue);
                if (parsed === null) error = "number";
                else value = parsed;
            } else {
                if (col.required && rawValue === "") error = "required";
                else if (rawValue !== "" && col.lookup && lookupSets[col.lookup] && !lookupSets[col.lookup].has(rawValue)) {
                    error = "lookup";
                } else if (
                    rawValue !== "" &&
                    col.newIfMissing &&
                    lookupSets[col.newIfMissing] &&
                    !lookupSets[col.newIfMissing].has(rawValue)
                ) {
                    // นำเข้าได้ตามปกติ แค่บอกให้เห็นว่าแถวนี้จะทำให้เกิดข้อมูลใหม่ใน master
                    note = NEW_NOTES[col.newIfMissing];
                }
            }

            if (error) valid = false;
            cells[col.key] = { raw: rawValue, value, error, ...(note && { note }) };
        });

        // Storage Location ดูเป็นคู่กับ Plant — เป็นการดูข้ามคอลัมน์ จึงทำหลังตรวจรายช่องจบ
        // (รหัสพื้นที่เก็บซ้ำข้ามคลังได้ แค่มีรหัสอยู่จริงยังไม่พอ ต้องเป็นของคลังนี้ด้วย)
        // ว่าง = นำเข้าได้ สต็อกไม่ผูกพื้นที่เก็บ / คลังนี้ยังไม่มีรหัสนี้ = หลังบ้านสร้างพื้นที่เก็บให้ตอน commit
        const plantCell = cells.plant_code;
        const slocCell = cells.storage_location_code;
        if (!plantCell.error && plantCell.value && slocCell.value && lookupSets.locationPlants) {
            const owner = lookupSets.locationPlants.get(slocCell.value);
            // shared = มีตัวที่ไม่ผูกคลังอยู่ด้วย ใช้กับคลังไหนก็ได้ (หลังบ้านใช้เป็นตัวสำรอง)
            if (!owner || (!owner.shared && !owner.plants.has(plantCell.value))) {
                slocCell.note = NEW_NOTES.location;
            }
        }

        // คีย์ซ้ำในไฟล์เดียวกัน = ข้อมูลผิด (แถวเดิมจะทับกันตอนบันทึก)
        const key = KEY_COLUMNS.map((k) => norm(cells[k]?.value).toUpperCase()).join("|");
        if (seenKeys.has(key)) {
            cells.batch_number.error = "duplicate";
            valid = false;
        } else {
            seenKeys.add(key);
        }

        rows.push({ row_no: STOCK_IMPORT_HEADER_ROWS + idx + 1, valid, cells });
    });

    return { rows, sheetName };
};

/** แถวที่ผ่านการตรวจ -> payload ที่ backend รับ */
const toPayload = (row) => {
    const value = (key) => row.cells[key]?.value ?? "";
    return {
        row_no: row.row_no,
        material_code: value("material_code"),
        material_name: value("material_name"),
        plant_code: value("plant_code"),
        plant_name: value("plant_name"),
        storage_location_code: value("storage_location_code"),
        storage_location_name: value("storage_location_name"),
        special_stock_type: value("special_stock_type"),
        special_stock_type_name: value("special_stock_type_name"),
        wbs_element: value("wbs_element"),
        wbs_element_name: value("wbs_element_name"),
        batch_number: value("batch_number"),
        unrestricted_qty: Number(value("unrestricted_qty")) || 0,
        quality_inspection_qty: Number(value("quality_inspection_qty")) || 0,
        blocked_qty: Number(value("blocked_qty")) || 0,
    };
};

/**
 * นำเข้าจริงแบบ full-replace
 *
 *   1. เปิดรอบ (batch)   - ยังไม่แตะข้อมูลจริง
 *   2. ส่งทีละ chunk     - เข้าตารางพัก ส่งเรียงทีละชุดเพื่อให้ error ชี้แถวได้ตรง
 *   3. commit            - จุดเดียวที่ข้อมูลจริงถูกเปลี่ยน (ลบของเดิมทั้งบริษัทแล้วสร้างใหม่ใน transaction เดียว)
 *
 * พังตรงไหนก็ยกเลิกรอบทิ้ง สต็อกเดิมยังอยู่ครบทุกกรณี
 *
 * @param {{rows: Array, fileName?: string, sheetName?: string, onProgress?: (percent: number, done: number, total: number) => void}} params
 * @returns {Promise<{ok: boolean, summary?: object, message?: string, errors?: string[]}>}
 */
export const runStockImport = async ({ rows, fileName = "", sheetName = STOCK_IMPORT_SHEET, onProgress }) => {
    const companyId = company?.id || "";
    const payloads = rows.map(toPayload);
    const total = payloads.length;

    const chunks = [];
    for (let i = 0; i < total; i += STOCK_IMPORT_CHUNK_SIZE) {
        chunks.push(payloads.slice(i, i + STOCK_IMPORT_CHUNK_SIZE));
    }

    // ยอดรวมของไฟล์ ส่งไปให้หลังบ้านเทียบตอน commit ว่าที่เขียนลงจริงตรงกับต้นทางไหม
    // จำนวนแถวอย่างเดียวเช็กไม่พอ เพราะหลายแถวถูกยุบเป็นสต็อกแถวเดียว ยอดเพี้ยนแล้วแถวยังครบได้
    const sumOf = (key) => Math.round(payloads.reduce((acc, row) => acc + Number(row[key] || 0), 0) * 100) / 100;

    const opened = await POST(BASE_PATH, {
        company_id: companyId,
        file_name: fileName,
        sheet_name: sheetName,
        expected_rows: total,
        expected_unrestricted_qty: sumOf("unrestricted_qty"),
        expected_quality_inspection_qty: sumOf("quality_inspection_qty"),
        expected_blocked_qty: sumOf("blocked_qty"),
    });
    if (!opened?.status || !opened?.data?.uid) return { ok: false, message: opened?.message };

    const batchId = opened.data.uid;
    // DELETE helper ไม่ส่ง body -> ต้องแนบ company_id ไปกับ query string
    const cancelBatch = () => DELETE(`${BASE_PATH}/${batchId}?company_id=${encodeURIComponent(companyId)}`);

    let done = 0;
    for (const chunk of chunks) {
        const res = await POST(`${BASE_PATH}/${batchId}/rows`, { company_id: companyId, rows: chunk });
        if (!res?.status) {
            await cancelBatch();
            return { ok: false, message: res?.message, errors: Array.isArray(res?.errors) ? res.errors : [] };
        }
        done += chunk.length;
        // กันโควตาไว้ 10% ให้ขั้น commit
        if (onProgress) onProgress(Math.round((done / total) * 90), done, total);
    }

    const committed = await POST(`${BASE_PATH}/${batchId}/commit`, { company_id: companyId });
    if (!committed?.status) {
        await cancelBatch();
        return { ok: false, message: committed?.message };
    }

    if (onProgress) onProgress(100, total, total);
    return { ok: true, summary: committed.data || {} };
};

export default {
    STOCK_IMPORT_COLUMNS,
    STOCK_IMPORT_SHEET,
    STOCK_IMPORT_CHUNK_SIZE,
    parseStockWorkbook,
    runStockImport,
};
